import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronDown, TreePine } from 'lucide-react';
import { toast } from 'sonner';
import { calculatePricing } from '../services/pricingService';

const DeckCalculatorPanel = forwardRef(({
  isOpen, onToggle, measurementTotals, manualOverride, setManualOverride,
  onServiceTotalChange, initialInputs, onInputsChange,
}, ref) => {
  const [deckData, setDeckData] = useState(
    initialInputs && Object.keys(initialInputs).length
      ? { sqFt: '', material: 'wood', ...initialInputs }
      : { sqFt: '', material: 'wood' }
  );

  const onInputsChangeRef = useRef(onInputsChange);
  onInputsChangeRef.current = onInputsChange;
  useEffect(() => { onInputsChangeRef.current?.('deck', deckData); }, [deckData]);

  // Auto-fill from measurement if panel is open and user hasn't manually edited
  useEffect(() => {
    if (!isOpen) return;
    const area = measurementTotals?.areaSqFt || 0;
    // Only auto-fill if: area > 0 AND user hasn't manually overridden
    if (area > 0 && !manualOverride.deckSqFt) {
      // Round to whole number for clean UX display
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional sync from external measurement source
      setDeckData((prev) => ({ ...prev, sqFt: Math.round(area) }));
    }
  }, [isOpen, measurementTotals?.areaSqFt, manualOverride.deckSqFt]);

  const handleCalculate = async () => {
    const sq = Number(deckData.sqFt);
    if (!Number.isFinite(sq) || sq <= 0) {
      toast.error('Please enter deck square footage.');
      return;
    }

    const resp = await calculatePricing('deck', {
      data: {
        sqFt: sq,
        material: deckData.material,
      },
      discount: 0,
    });

    if (!resp.success || !resp.quote) {
      toast.error(resp.message || 'Service pricing not implemented yet, but UI remains active.');
      onServiceTotalChange('deck', { subtotal: 0, tax: 0, grandTotal: 0 });
      return;
    }

    const quote = resp.quote;
    const nowIso = new Date().toISOString();

    onServiceTotalChange('deck', {
      input: {
        sqFt: sq,
        material: deckData.material,
      },
      result: {
        serviceTotal: quote.serviceTotal || quote.subtotal || 0,
        adjustedSqFt: quote.adjustedSqFt || sq,
        discount: quote.discount || 0,
        tax: quote.tax || 0,
        subtotal: quote.subtotal || quote.serviceTotal || 0,
        grandTotal: quote.grandTotal || 0,
      },
      measurement: {
        polygons: null,
        linearFeet: null,
        sqFt: sq,
        computedAt: nowIso,
      },
    });
  };

  useImperativeHandle(ref, () => ({
    recalc: handleCalculate,
    getCurrentInputs: () => deckData,
  }), [deckData]);

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <Card className="overflow-hidden">
        <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-secondary/10 rounded-lg">
              <TreePine className="h-5 w-5 text-secondary" />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-bold text-secondary">🪵 Decks / Patios</h3>
              <p className="text-sm text-muted-foreground">Configure deck specifications</p>
            </div>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-muted-foreground transition-transform ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="p-6 space-y-4 bg-muted/30">
            {/* Measured Area */}
            <div className="space-y-2">
              <Label htmlFor="deck-area">Measured Area (sq ft)</Label>
              <Input
                id="deck-area"
                type="number"
                placeholder="Enter square footage"
                className="font-mono"
                value={deckData.sqFt}
                onChange={(e) => {
                  setManualOverride(prev => ({ ...prev, deckSqFt: true }));
                  setDeckData(prev => ({ ...prev, sqFt: e.target.value }));
                }}
              />
            </div>

            {/* Discount removed at service level; handled in Quote Summary */}

            {/* Material Type */}
            <div className="space-y-2">
              <Label htmlFor="deck-material">Material Type</Label>
              <Select>
                <SelectTrigger id="deck-material">
                  <SelectValue placeholder="Select material" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wood">Wood</SelectItem>
                  <SelectItem value="composite">Composite</SelectItem>
                  <SelectItem value="concrete">Concrete Patio</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Calculate Button */}
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              size="lg"
              onClick={handleCalculate}
            >
              Calculate Deck Cost
            </Button>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
});

export default DeckCalculatorPanel;
